import { css, styled } from 'styled-components'

import { Typography } from '../../Typography/Typography'

import type { ChoiceCardColorVariant } from './ChoiceCardVertical'

export const StyledButton = styled.button<{
  $isHighlighted: boolean
  $colorVariant: ChoiceCardColorVariant
}>`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: flex-end;
  padding: 0;
  border: none;
  background-color: initial;
  cursor: pointer;
  gap: 0;
  outline: none;

  &[disabled] {
    cursor: not-allowed;
    pointer-events: none;
  }
`

export const StyledImageWrapper = styled.div<{
  $isHighlighted: boolean
  $colorVariant: ChoiceCardColorVariant
  $isDisabled?: boolean
}>`
  overflow: hidden;
  border-radius: ${({ theme }) =>
    `${theme.radius['Radius LG']}px ${theme.radius['Radius LG']}px 0 0`};
  background-color: ${({ theme }) => theme.colors['N Outline/Primary']};
  transition: all 0.3s ease;

  ${({ $isHighlighted, $colorVariant, $isDisabled, theme }) => {
    if ($isDisabled) {
      return css`
        border: 2px solid ${theme.colors['N Outline/Primary']};
        border-bottom: none;
      `
    }

    if ($colorVariant === 'blue2') {
      if ($isHighlighted) {
        return css`
          border: 2px solid ${theme.colors['N Outline/Brand Selected']};
          border-bottom: none;
        `
      }

      return css`
        border: 2px solid ${theme.colors['N Outline/Primary']};
        border-bottom: none;
      `
    } else {
      //other color variants
      if ($isHighlighted)
        return css`
          border: 2px solid ${theme.colors['N Outline/Brand Selected']};
          border-bottom: none;
        `

      return css`
        border: 2px solid ${theme.colors['N Outline/Primary']};
        border-bottom: none;

        @media (hover: hover) and (pointer: fine) {
          ${StyledButton}:hover & {
            border: 2px solid ${theme.colors['N Outline/Brand Hover']};
            border-bottom: none;
          }
        }
      `
    }
  }}
`

export const StyledImageContainer = styled.div<{
  $isHighlighted: boolean
  $colorVariant: ChoiceCardColorVariant
}>`
  width: 100%;
  height: 100%;
  border-radius: ${({ theme }) =>
    `0 0 ${theme.radius['Radius XS']}px ${theme.radius['Radius XS']}px`};
  background-color: ${({ theme, $colorVariant }) => {
    switch ($colorVariant) {
      case 'lightBlue':
        return theme.colors['N Surface/Brand']
      case 'grey':
        return theme.colors['N Surface/Base']
      case 'blue2':
        return theme.colors['N Surface/Primary']
      default:
        return theme.colors['N Surface/Primary']
    }
  }};

  img {
    width: 100%;
    height: 100%;
  }
`

export const StyledCardSection = styled.div<{
  $isHighlighted: boolean
  $colorVariant: ChoiceCardColorVariant
  $isDisabled?: boolean
}>`
  display: flex;
  width: 100%;
  flex-grow: 1;
  align-items: center;
  justify-content: space-between;
  padding: 4px;
  border-radius: ${({ theme }) => {
    return `0 0 ${theme.radius['Radius LG']}px ${theme.radius['Radius LG']}px`
  }};
  gap: 4px;
  justify-items: flex-start;
  text-align: left;
  transition: all 0.3s ease;

  ${({ $isHighlighted, $colorVariant, $isDisabled, theme }) => {
    if ($isDisabled && $colorVariant === 'blue2') {
      return css`
        border: 2px solid ${theme.colors['N Button/Disabled']};
        border-top: none;
        background-color: ${theme.colors['N Button/Disabled']};
      `
    }

    if ($colorVariant === 'blue2') {
      if ($isHighlighted) {
        return css`
          border: 2px solid ${theme.colors['N Outline/Brand Selected']};
          border-top: none;
          background-color: ${theme.colors['N Button/Enable']};
        `
      } else {
        return css`
          border: 2px solid ${theme.colors['N Outline/Brand Selected']};
          border-top: none;
          background-color: ${theme.colors['N Button/Enable']};

          @media (hover: hover) and (pointer: fine) {
            ${StyledButton}:hover & {
              border: 2px solid ${theme.colors['N Button/Hover']};
              border-top: none;
              background-color: ${theme.colors['N Button/Hover']};
            }
          }
        `
      }
    } else {
      //other color variants
      if ($isHighlighted)
        return css`
          border: 2px solid ${theme.colors['N Outline/Brand Selected']};
          border-top: none;
          background-color: ${theme.colors['N Surface/Primary']};
        `
    }

    return css`
      border: 2px solid ${theme.colors['N Outline/Primary']};
      border-top: none;
      background-color: ${theme.colors['N Surface/Primary']};

      @media (hover: hover) and (pointer: fine) {
        ${StyledButton}:hover & {
          border: 2px solid ${theme.colors['N Outline/Brand Hover']};
          border-top: none;
        }
      }
    `
  }}
`

export const StyledTitleTypography = styled(Typography)`
  min-width: 0;
  flex: 1;
  padding: 4px 0 4px 8px;
  hyphens: auto;
  overflow-wrap: break-word;
`

export const StyledChoice = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-self: center;
`
